// ============================================================================
// 📁 FILE: src/modules/ticket/components/WorkflowControls.js
// Hidden workflow engine for StatusBlock
// Exposes loadTransitions() + triggerStatusChange()
// Shows modal only when rule requires extra input
// ============================================================================

import React, {
  forwardRef,
  useImperativeHandle,
  useState
} from "react";

import { Modal, Form, Input, message } from "antd";
import axiosClient from "../../../api/axiosClient";

const WorkflowControls = forwardRef(function WorkflowControls(
  { ticketId },
  ref
) {
  const [transitions, setTransitions] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  // -----------------------------------------------------------
  // 1️⃣ LOAD TRANSITIONS — called from StatusBlock
  // -----------------------------------------------------------
  async function loadTransitions(ticketId) {
    if (!ticketId) return [];

    try {
      const res = await axiosClient.get(
        `/ticket-management/tickets/${ticketId}/workflow`
      );

      if (res.data?.success) {
        const rules = res.data.possible_transitions || [];
        setTransitions(rules);
        return rules;
      } else {
        message.error(res.data?.message || "Failed to load workflow rules");
        return [];
      }
    } catch (err) {
      console.error("loadTransitions error:", err);
      message.error("Workflow rules fetch error");
      return [];
    }
  }

  // -----------------------------------------------------------
  // 2️⃣ TRIGGER STATUS CHANGE — called from StatusBlock button
  // -----------------------------------------------------------
  function triggerStatusChange({ ticketId, rule, onUpdated }) {
    if (!rule) {
      message.warning("Select a valid status");
      return;
    }

    const cond = rule.conditions || {};
    const needsModal =
      cond.require_resolution_notes ||
      cond.require_reopen_reason ||
      cond.must_be_assignee;

    setSelectedRule(rule);

    if (needsModal) {
      form.resetFields();

      if (rule.auto_assign_to_user_id) {
        form.setFieldsValue({
          assigned_to_id: rule.auto_assign_to_user_id,
        });
      }

      setModalVisible(true);
    } else {
      applyTransition({ ticketId, rule, payload: {}, onUpdated });
    }
  }

  // -----------------------------------------------------------
  // 3️⃣ APPLY TRANSITION (PUT API)
  // -----------------------------------------------------------
  async function applyTransition({ ticketId, rule, payload, onUpdated }) {
    setLoading(true);

    try {
      const body = {
        target_status: rule.to_status,
        ...payload,
      };

      const res = await axiosClient.put(
        `/ticket-management/tickets/${ticketId}/status`,
        body
      );

      if (res.data?.success) {
        message.success(res.data.message || "Status updated");
        setModalVisible(false);
        form.resetFields();

        await loadTransitions(ticketId);

        if (typeof onUpdated === "function") {
          onUpdated(res.data.ticket);
        }
      } else {
        message.error(res.data?.message || "Update failed");
      }
    } catch (err) {
      console.error("applyTransition error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Status update failed";
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }

  // -----------------------------------------------------------
  // 4️⃣ MODAL SUBMIT HANDLER
  // -----------------------------------------------------------
  async function onModalFinish(values) {
    const payload = {};

    if (values.assigned_to_id)
      payload.assigned_to_id = values.assigned_to_id;

    if (values.resolution_notes)
      payload.resolution_notes = values.resolution_notes;

    if (values.reopen_reason)
      payload.reopen_reason = values.reopen_reason;

    applyTransition({
      ticketId,
      rule: selectedRule,
      payload,
      onUpdated: values.onUpdated,
    });
  }

  // -----------------------------------------------------------
  // 5️⃣ CONDITIONAL MODAL FIELDS
  // -----------------------------------------------------------
  function renderModalFields() {
    if (!selectedRule) return null;

    const cond = selectedRule.conditions || {};

    return (
      <>
        {/* Optional or required assignment */}
        <Form.Item label="Assign to Employee ID" name="assigned_to_id">
          <Input placeholder="Enter employee ID" />
        </Form.Item>

        {/* Resolution Notes */}
        {cond.require_resolution_notes ? (
          <Form.Item
            label="Resolution Notes"
            name="resolution_notes"
            rules={[{ required: true, message: "Resolution notes required" }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        ) : null}

        {/* Reopen Reason */}
        {cond.require_reopen_reason ? (
          <Form.Item
            label="Reopen Reason"
            name="reopen_reason"
            rules={[{ required: true, message: "Reopen reason required" }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        ) : null}
      </>
    );
  }

  // -----------------------------------------------------------
  // 6️⃣ EXPOSE METHODS TO PARENT (StatusBlock)
  // -----------------------------------------------------------
  useImperativeHandle(ref, () => ({
    loadTransitions,
    triggerStatusChange,
  }));

  // -----------------------------------------------------------
  // 7️⃣ RENDER ONLY MODAL
  // -----------------------------------------------------------
  return (
    <Modal
      title={
        selectedRule
          ? `Move to ${selectedRule.to_status.replace(/_/g, " ")}`
          : "Workflow Action"
      }
      open={modalVisible}
      onCancel={() => setModalVisible(false)}
      okText="Confirm"
      confirmLoading={loading}
      onOk={() => form.submit()}
    >
      <Form form={form} layout="vertical" onFinish={onModalFinish}>
        {renderModalFields()}
      </Form>
    </Modal>
  );
});

export default WorkflowControls;
